import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useRequest } from '@frontend/api';

import { MFG_QUERY_KEYS } from './constants';
import type {
  AdskMfgQueryResource,
  FusionHub,
  AdskMfgParams,
  AdskMfgQueryResponse,
  AdskMfgInfiniteQueryFunction,
} from './types';
import { adskRequest, isAdskMfgError } from './utils';

const GRAPHQL_QUERY = {
  operationName: 'GetHubs',
  query: `query GetHubs($pagination: PaginationInput) {
    hubs(pagination: $pagination) {
      pagination {
        cursor
      }
      results {
        name
        id
      }
    }
  }`,
};

type DataType = AdskMfgQueryResource<FusionHub[]>;
type ReturnType = { hubs: DataType };

type ListHubsQueryKey = readonly [typeof MFG_QUERY_KEYS.LIST_HUBS];

export const useListHubs: AdskMfgInfiniteQueryFunction<
  AdskMfgQueryResponse<ReturnType>,
  [AdskMfgParams],
  ListHubsQueryKey,
  FusionHub
> = (
  { accessToken, refreshAccess, pagination = { cursor: null } },
  { queryOptions } = {},
) => {
  const queryClient = useQueryClient();
  const request = useRequest();
  const results = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- adskRequest should be built with a hook, pagination should be added to the query key
    queryKey: [MFG_QUERY_KEYS.LIST_HUBS],
    queryFn: ({ signal, pageParam }) =>
      adskRequest<DataType, 'hubs'>(
        accessToken ?? '',
        {
          ...GRAPHQL_QUERY,
          variables: {
            pagination: { ...pagination, cursor: pageParam },
          },
        },
        request,
        { init: { signal } },
        (data) => !!data.hubs,
        (msg) => `Failed to list hubs: ${msg}`,
      ),
    enabled: !!accessToken,
    getNextPageParam: (currentPage) =>
      currentPage.data.hubs.pagination.cursor === null
        ? null
        : currentPage.data.hubs.pagination.cursor,
    ...queryOptions,
    onError: async (error) => {
      // If not an ADSK error, bail
      if (!isAdskMfgError(error)) return;

      // Trigger refresh on 401s
      if (error.statusCode === 401) {
        await refreshAccess();

        // Invalidate query to trigger a new request
        queryClient.invalidateQueries({ queryKey: MFG_QUERY_KEYS.LIST_HUBS });
        return;
      }

      // Run the passed in onError if provided
      queryOptions?.onError?.(error);
    },
  });

  const flatData = useMemo(
    () => results?.data?.pages?.flatMap((page) => page.data.hubs.results),
    [results?.data?.pages],
  );

  const flatErrors = useMemo(
    () => results?.data?.pages?.flatMap((page) => page?.errors ?? []),
    [results?.data?.pages],
  );

  return { ...results, flatData, flatErrors };
};
